/**
 * Customer Sessions API Client
 * Handles server-side session lifecycle operations
 * These endpoints manage the full charging session from creation to completion
 */

import axios from 'axios';
import { applyFromSession, applySnapshot } from '../chargerUx/canonicalStore';
import { v4 as uuidv4 } from './uuid.js';
import {
  AUTHORIZATION_DENIED,
  authorizationDeniedCopy,
  authorizationDeniedMessage,
  effectivePricingSnapshot,
} from '../utils/sessionAuthorization.js';
import { isAccessOutcome } from '../utils/accessOutcome.js';

const API_BASE = import.meta.env.VITE_API_URL;
const SESSIONS_BASE = `${API_BASE}/ev/sessions`;

/**
 * Generate correlation ID for request tracking
 */
function getCorrelationId() {
  return `ev-user-${Date.now()}-${Math.random().toString(36).substring(7)}`;
}

/**
 * Clear session persistence from localStorage
 * Used in all fail/cancel paths to ensure clean state
 */
export function clearSessionPersistence() {
  try {
    localStorage.removeItem('ev.session.v1');
    localStorage.removeItem('activeSessionUid');
    localStorage.removeItem('ev:lastActiveSession'); // Clear rehydration key
    console.log('[SessionsAPI] Cleared session persistence');
  } catch (error) {
    console.error('[SessionsAPI] Failed to clear session persistence:', error);
  }
}

/**
 * Normalize API error responses to consistent format
 * Strips large HTML from error details and maps to user-friendly messages
 * 
 * @param {object} error - Axios error object
 * @param {string} correlationId - Request correlation ID
 * @returns {object} Normalized error { code, message, correlationId, rawStatus }
 */
export function normalizeApiError(error, correlationId) {
  const errorData = error.response?.data;
  const errorCode = errorData?.error || 'UNKNOWN_ERROR';
  const rawStatus = error.response?.status;
  
  // Strip large HTML from details (common in OCPP 404 errors)
  let details = errorData?.details || '';
  if (typeof details === 'string' && details.includes('<html')) {
    details = ''; // Strip HTML error pages
  }
  
  // Map error codes to user-friendly messages
  const errorMessages = {
    // Administrative maintenance override — the backend rejects
    // session-start with this code when the admin has placed the
    // charger under maintenance. Kept lower-cased to match the API
    // contract in station-under-maintenance responses.
    'station_under_maintenance': 'This charger is currently under maintenance. Please use another charger nearby.',
    'OCPP_ERROR': "We couldn't start the charger. Please try again.",
    'OCPP_START_FAILED': "We couldn't start the charger. Please try again.",
    'OCPP_START_REJECTED': "The charger refused the start request. Please make sure the cable is connected, then try again.",
    'CONNECTOR_BUSY': "This charger is currently in use. Please wait and try again.",
    'SESSION_NOT_FOUND': "This session expired. Please start again.",
    'SESSION_EXPIRED': "This session expired. Please start again.",
    'PAYMENT_FAILED': "Payment authorization failed. Please try again.",
    // C-02: the API refuses to start a card-billed session that has no
    // authorized card hold (402). Nothing was sent to the charger.
    'PAYMENT_HOLD_REQUIRED': "Your card has not been authorized for this session, so charging was not started. Please start again and authorize your card.",
    'INVALID_SESSION_STATE': "Session is in an invalid state. Please start again.",
    // The two idTag-related errors only fire when the start request was
    // misconfigured by an older client build (sending EV_USER) or when
    // a real PIN was rejected. Either way the end-user just needs the
    // cable-connection nudge — the technical detail belongs in logs.
    'INVALID_ID_TAG': "Unable to start charging. Please make sure the cable is connected, then try again.",
    'ID_TAG_REJECTED': "Unable to start charging. Please make sure the cable is connected, then try again.",
    // Backend refused to return a session UID because the guest recovery
    // credential could not be issued atomically with the session INSERT.
    // Never advance the UI on this — the session was rolled back.
    'SESSION_CREDENTIAL_ISSUANCE_FAILED': "We couldn't securely start this charging session. Please try again.",
    // Defence in depth: guarded reads/mutations return this if the
    // recovery cookie is missing/invalid on later requests. The UI
    // must not enter polling/charging state — surface a clear retry.
    'SESSION_AUTH_REQUIRED': "This charging session couldn't be verified. Please start again.",
  };
  
  // P4b: the session-start authorizer refused (reasonCode is a §8.7
  // code). Driver-facing copy per code; never the API message, which is
  // written for operators.
  const reasonCode = errorData?.reasonCode || null;
  const userMessage = errorCode === AUTHORIZATION_DENIED
    ? authorizationDeniedMessage(reasonCode)
    : (errorMessages[errorCode] || errorData?.message || error.message || 'An unexpected error occurred');
  
  console.log('[SessionsAPI] Normalized error:', {
    code: errorCode,
    message: userMessage,
    correlationId: errorData?.correlationId || correlationId,
    rawStatus,
    originalError: errorCode
  });
  
  return {
    code: errorCode,
    message: userMessage,
    correlationId: errorData?.correlationId || correlationId,
    rawStatus,
    ...(reasonCode ? { reasonCode } : {}),
    details: details ? details.substring(0, 200) : undefined // Keep first 200 chars of details if present
  };
}

/**
 * Create a new customer session on the server
 * This MUST be called before any payment or charging operations
 *
 * @param {object} params - Session creation parameters
 * @param {string} params.stationId - EV charger station ID
 * @param {number} params.connectorId - Physical connector ID (>=1). REQUIRED.
 *   No default. On a multi-connector charger the customer must have
 *   explicitly selected which physical connector they plugged into
 *   before this function is called (see EvProfile's connector picker).
 *   On a single-connector charger the auto-select in EvProfile fills
 *   this in with the sole connector's id. Missing / non-numeric /
 *   < 1 rejects locally with INVALID_CONNECTOR_ID so a client bug
 *   cannot silently route every customer to connector 1.
 * @param {object} params.pricingSnapshot - Pricing information at time of session creation
 * @param {number} params.pricingSnapshot.rate_per_kwh - Rate per kWh
 * @param {number} params.pricingSnapshot.session_fee - Session fee
 * @param {string} params.pricingSnapshot.currency - Currency code (e.g., 'BBD', 'USD')
 * @param {number} params.pricingSnapshot.min_charge_amount - Minimum charge amount
 * @returns {Promise<object>} Response with session_uid and session details
 */
/**
 * M5 — Guest recovery by emailed code.
 *
 *   POST /api/guest/sessions/:sessionUid/recovery/request  {}
 *     The server sends the code to the address already on this session's
 *     receipt; the app never sends or sees an email.
 *     200 { ok, sent: true, resendAvailableAt }                sent
 *     200 { ok, sent: false, reason: 'COOLDOWN', resendAvailableAt }
 *     409 RECOVERY_UNAVAILABLE (nowhere to send) · 429 RECOVERY_RATE_LIMITED
 *     503 RECOVERY_DELIVERY_FAILED · 500
 *   POST /api/guest/sessions/:sessionUid/recovery/verify   { code }
 *     200 { ok } + Set-Cookie: a fresh HttpOnly credential for this session
 *     401 RECOVERY_FAILED (wrong / expired / used code) · 429 · 500
 *
 * Both are credentialed (withCredentials) so the verify response can set
 * the cookie that re-authorises /api/ev/sessions/:uid/* for this browser.
 * Nothing from the lost browser state is needed.
 *
 * Failures return `{ ok: false, status, error }`; `error` is the backend
 * code (or NETWORK_ERROR). The view turns it into a safe message.
 */
function recoveryFailure(error) {
  const status = error?.response?.status;
  return {
    ok: false,
    status,
    error: error?.response?.data?.error || (status ? `HTTP_${status}` : 'NETWORK_ERROR'),
  };
}

export async function requestGuestSessionOtp(sessionUid) {
  try {
    const response = await axios.post(
      `${API_BASE}/guest/sessions/${encodeURIComponent(sessionUid)}/recovery/request`,
      {},
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    );
    return { ok: true, data: response.data };
  } catch (error) {
    return recoveryFailure(error);
  }
}

export async function verifyGuestSessionOtp(sessionUid, { code }) {
  try {
    const response = await axios.post(
      `${API_BASE}/guest/sessions/${encodeURIComponent(sessionUid)}/recovery/verify`,
      { code },
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    );
    return { ok: true, data: response.data };
  } catch (error) {
    return recoveryFailure(error);
  }
}

/**
 * Failure envelope shared by create / attach vehicle / start. A 403
 * AUTHORIZATION_DENIED (P4b) carries reasonCode + authorization and gets
 * driver-facing copy; everything else keeps the API message as before.
 *
 * Platform cleanup, Phase 3: a refusal by the charger's access settings
 * also carries `accessOutcome` (greencharge.access-outcome.v1). It is
 * passed through untouched; the caller routes the driver to the
 * FLEET_ACCESS_RESULT step, which prints the backend's own title and
 * message. The reason-code copy in `message` is then only the fallback
 * for an API that sends no `accessOutcome`.
 */
function authorizationAwareFailure(error, fallbackCode, fallbackMessage, correlationId) {
  const data = error?.response?.data || {};
  const code = data.error || fallbackCode;
  const denied = code === AUTHORIZATION_DENIED;
  return {
    ok: false,
    httpStatus: error?.response?.status ?? null,
    error: code,
    message: denied
      ? authorizationDeniedMessage(data.reasonCode)
      : (data.message || error?.message || fallbackMessage),
    ...(denied
      ? {
        reasonCode: data.reasonCode || null,
        authorization: data.authorization || null,
        offerCard: authorizationDeniedCopy(data.reasonCode).offerCard,
        ...(isAccessOutcome(data.accessOutcome) ? { accessOutcome: data.accessOutcome } : {}),
      }
      : {}),
    correlationId: data.correlationId || correlationId,
  };
}

/**
 * The charging access outcome for a charger and the vehicle the driver has
 * just proven (`greencharge.access-outcome.v1`; sw-ev-api
 * docs/CHARGER_ACCESS_OUTCOME.md). Called after vehicle verification and
 * before a session is created or the vehicle is attached. Nothing is written
 * server-side.
 *
 * Proof is the same as session creation: the signed-in owner's token (added
 * to every request by the shared axios interceptor in main.jsx, exactly as
 * for createCustomerSession) or, for a guest, the vehicle's PIN.
 *
 * The outcome is returned as received. This client reads no access rule and
 * no reason code out of it.
 *
 * @returns {Promise<{ok: true, accessOutcome: object, correlationId: string}
 *   | {ok: false, error: string, message: string, httpStatus: number|null, correlationId: string}>}
 *   `httpStatus` is null when the request never got an answer.
 */
export async function checkChargingAccess({ stationId, vehicleId, vehiclePin } = {}) {
  const correlationId = getCorrelationId();
  try {
    const body = { stationId };
    if (vehicleId) body.vehicleId = vehicleId;
    // The PIN only means something next to the vehicle it proves.
    if (vehicleId && vehiclePin) body.vehiclePin = String(vehiclePin).trim();

    const response = await axios.post(
      `${SESSIONS_BASE}/access-check`,
      body,
      { headers: { 'Content-Type': 'application/json', 'X-Correlation-Id': correlationId } },
    );
    const accessOutcome = response?.data?.accessOutcome;
    if (!isAccessOutcome(accessOutcome)) {
      // A 200 without an outcome is not an answer (an older API, a proxy).
      return {
        ok: false,
        error: 'ACCESS_CHECK_UNAVAILABLE',
        message: 'The access check returned no outcome.',
        httpStatus: response?.status ?? null,
        correlationId: response?.data?.correlationId || correlationId,
      };
    }
    return { ok: true, accessOutcome, correlationId: response.data.correlationId || correlationId };
  } catch (error) {
    const data = error?.response?.data || {};
    return {
      ok: false,
      error: data.error || (error?.response ? 'ACCESS_CHECK_FAILED' : 'NETWORK_ERROR'),
      message: data.message || error?.message || 'The access check could not be completed.',
      httpStatus: error?.response?.status ?? null,
      correlationId: data.correlationId || correlationId,
    };
  }
}

export async function createCustomerSession({
  stationId, connectorId, pricingSnapshot, redeemCode,
  // Optional plate attribution. When the ChargeSetup step captured a
  // saved vehicle we send vehicleId (source: 'saved_vehicle'); when
  // the driver typed a plate manually we send licensePlate (source:
  // 'manual_entry'). Backend applies the ownership + normalisation
  // rules in plateAttributionService — the client never has to
  // duplicate that logic.
  vehicleId, licensePlate, plateCaptureSource,
  // Guest vehicle PIN. Sent alongside a caller-supplied vehicleId in
  // the guest flow so the backend re-verifies the PIN against THIS
  // specific vehicleId (defence against a swapped-vehicleId POST after
  // /vehicles/verify). Ignored server-side when the caller is
  // authenticated.
  vehiclePin,
}) {
  const correlationId = getCorrelationId();

  // connectorId is REQUIRED. The old default `connectorId = 1`
  // silently routed every multi-connector customer to connector 1
  // regardless of which physical port they plugged into. If we get
  // here without a valid connectorId something upstream (EvProfile
  // picker / SessionContext) has failed to gate on selection —
  // rejecting locally with a discriminable error means we don't
  // create a mis-routed session on the backend.
  const parsedConnectorId = typeof connectorId === 'number'
    ? connectorId
    : parseInt(String(connectorId ?? ''), 10);
  if (!Number.isInteger(parsedConnectorId) || parsedConnectorId < 1) {
    console.error('[SessionsAPI] Refused to create session — invalid connectorId', {
      stationId,
      receivedConnectorId: connectorId,
    });
    return {
      ok: false,
      error: 'INVALID_CONNECTOR_ID',
      message: 'Please select a connector before starting a session.',
    };
  }

  try {
    console.log('[SessionsAPI] Creating customer session:', {
      stationId,
      connectorId: parsedConnectorId,
      pricingSnapshot,
      hasRedeemCode: !!redeemCode,
      hasVehicleId: Boolean(vehicleId),
      hasLicensePlate: Boolean(licensePlate),
    });

    const body = {
      stationId: stationId,
      connectorId: parsedConnectorId,
      pricingSnapshot: pricingSnapshot,
      client: {
        source: 'ev_user'
      }
    };
    if (redeemCode) {
      body.redeemCode = String(redeemCode).trim().toUpperCase();
    }
    // Only include the plate keys when we actually have a value —
    // sending explicit nulls would pollute the API body and the debug
    // logs. The backend treats a missing key the same as null.
    if (vehicleId) body.vehicleId = vehicleId;
    if (licensePlate) body.licensePlate = String(licensePlate).trim();
    if (plateCaptureSource) body.plateCaptureSource = plateCaptureSource;
    // Only send the PIN when we actually have one AND a vehicleId to
    // bind it to — the backend ignores it otherwise, but omitting keeps
    // the request body (and debug logs) tidy.
    if (vehicleId && vehiclePin) body.vehiclePin = String(vehiclePin).trim();

    const response = await axios.post(
      `${SESSIONS_BASE}`,
      body,
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        }
      }
    );

    // Log the REAL response structure for debugging
    console.log('[SessionsAPI] raw axios response', { 
      status: response.status, 
      data: response.data, 
      keys: Object.keys(response.data || {}) 
    });

    // Extract session data - support multiple response shapes
    const payload = response.data;
    const sessionObj = payload?.session ?? payload;
    const sessionUid =
      sessionObj?.session_uid ??
      sessionObj?.sessionUid ??
      payload?.session_uid ??
      payload?.sessionUid;

    if (!sessionUid) {
      console.error('[SessionsAPI] create session parse failed', { payload });
      throw new Error('Session created but sessionUid missing from API response');
    }

    console.log('[SessionsAPI] Session created successfully', { 
      sessionUid, 
      status: sessionObj?.status 
    });

    return { 
      sessionUid, 
      session: sessionObj,
      // P4b: additive. `authorization` is present when the API ran the
      // session-start authorizer; `pricingSnapshot` is what to DISPLAY —
      // the server's rate columns when they are authoritative, else the
      // snapshot this app sent.
      authorization: sessionObj?.authorization ?? null,
      pricingSnapshot: effectivePricingSnapshot(sessionObj, pricingSnapshot),
    };
  } catch (error) {
    console.error('[SessionsAPI] Failed to create session:', error.response?.data || error.message);
    return authorizationAwareFailure(error, 'SESSION_CREATE_FAILED', 'Failed to create session', correlationId);
  }
}

/**
 * Get customer session details from server
 * Used for rehydration after page refresh or offline/online transitions
 * 
 * @param {string} sessionUid - Session UID
 * @returns {Promise<object>} Response with session details
 */
export async function getCustomerSession(sessionUid) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] Getting customer session:', sessionUid);

    const response = await axios.get(
      `${SESSIONS_BASE}/${sessionUid}`,
      {
        headers: {
          'X-Correlation-Id': correlationId
        }
      }
    );

    // Log raw response for debugging
    console.log('[SessionsAPI] raw axios response', {
      status: response.status,
      dataKeys: Object.keys(response.data || {}),
      sessionKeys: Object.keys(response.data?.session || {})
    });

    // Extract session data - support multiple response shapes
    // API might return: { session: {...} } OR just the session object directly
    const payload = response.data;
    const sessionObj = payload?.session ?? payload;

    // Validate we got a session object
    if (!sessionObj || typeof sessionObj !== 'object') {
      console.error('[SessionsAPI] Invalid session response - no session object found', { payload });
      return {
        ok: false,
        error: 'INVALID_RESPONSE',
        message: 'Invalid session response from server',
        correlationId
      };
    }

    // Charger UX: when the API offers a canonical snapshot, every session
    // read feeds the one store (first load, polling fallback, reconnect,
    // foreground, notification open) — it is applied only if it is newer.
    applyFromSession(sessionObj);

    console.log('[SessionsAPI] Session retrieved successfully:', {
      session_uid: sessionObj.session_uid ?? sessionObj.sessionUid,
      status: sessionObj.status
    });

    return {
      ok: true,
      session: sessionObj,
      correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] Failed to get session:', error.response?.data || error.message);
    const body = error.response?.data || {};

    // Handle 404 specially - session not found
    if (error.response?.status === 404) {
      return {
        ok: false,
        notFound: true,
        error: 'SESSION_NOT_FOUND',
        message: 'Session not found',
        // Forward the backend session_uid (when the API echoes it) so
        // the UI can show a support ref ops can grep for.
        session_uid: body.session_uid || sessionUid,
        correlationId: body.correlationId || correlationId
      };
    }

    // 410 SESSION_TERMINATED carries the structured failure_reason /
    // cancel_reason / status the polling layer needs to render a
    // specific user message ("cable not connected", "charger
    // unavailable", etc.) instead of a generic "did not respond in
    // time." Forward those fields verbatim.
    if (error.response?.status === 410) {
      // The ended session's canonical outcome, when the API sends it.
      if (body.canonical) applySnapshot(body.canonical);
      return {
        ok: false,
        terminated: true,
        error: body.error || 'SESSION_TERMINATED',
        message: body.message || `Session was ${body.status || 'terminated'}`,
        status: body.status || null,
        session_uid: body.session_uid || sessionUid,
        failure_reason: body.failure_reason || null,
        cancel_reason: body.cancel_reason || null,
        stopped_at: body.stopped_at || null,
        correlationId: body.correlationId || correlationId,
      };
    }

    return {
      ok: false,
      error: body.error || 'SESSION_GET_FAILED',
      message: body.message || error.message || 'Failed to get session',
      session_uid: body.session_uid || sessionUid,
      correlationId: body.correlationId || correlationId
    };
  }
}

/**
 * Patch receipt information on the session (EXPLICIT user submission)
 * This updates the customer's name and email for receipt delivery
 * The backend will send a "Receipt Details Saved" confirmation email
 * 
 * @param {string} sessionUid - Session UID
 * @param {object} receiptInfo - Receipt information
 * @param {string} receiptInfo.fullName - Customer's full name
 * @param {string} receiptInfo.email - Customer's email address
 * @returns {Promise<object>} Response with updated session and email status
 */
export async function patchReceipt(sessionUid, { fullName, email }) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] Patching receipt info for session:', sessionUid, '(explicit submission)');

    const response = await axios.patch(
      `${SESSIONS_BASE}/${sessionUid}/receipt`,
      {
        fullName: fullName,
        email: email
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[SessionsAPI] Receipt info patched successfully (confirmation email sent)');

    return {
      ok: true,
      session: response.data.session,
      email: response.data.email,
      correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] Failed to patch receipt:', error.response?.data || error.message);

    return {
      ok: false,
      error: error.response?.data?.error || 'RECEIPT_PATCH_FAILED',
      message: error.response?.data?.message || error.message || 'Failed to update receipt information',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Email the receipt for a finished session — POST /ev/sessions/:uid/receipt/email.
 *
 * This (not `patchReceipt`) is the call behind "Email me a receipt". PATCH
 * /receipt only stores a contact on the session and sends a one-off "receipt
 * details saved" notice; it never sends the receipt and it rewrites the
 * session's recorded contact. This endpoint renders the real receipt for
 * `sessionUid`, mails it to `email` without touching the stored contact, and
 * throttles repeats (60 s).
 *
 * Result:
 *   { ok: true,  resent: true,  emailedTo }
 *   { ok: true,  resent: false, throttled: true }       sent moments ago
 *   { ok: false, message, field?: 'email'|'fullName', status }
 *
 * @param {string} sessionUid
 * @param {{ email: string, fullName: string }} recipient
 */
export async function sendSessionReceiptEmail(sessionUid, { email, fullName }) {
  const correlationId = getCorrelationId();
  try {
    const response = await axios.post(
      `${SESSIONS_BASE}/${encodeURIComponent(sessionUid)}/receipt/email`,
      { email, fullName },
      { headers: { 'Content-Type': 'application/json', 'X-Correlation-Id': correlationId } },
    );
    const data = response.data || {};
    return {
      ok: true,
      resent: data.resent !== false && !data.throttled,
      throttled: !!data.throttled,
      emailedTo: data.emailed_to || email,
      correlationId,
    };
  } catch (error) {
    const data = error.response?.data || {};
    return {
      ok: false,
      status: error.response?.status || 0,
      error: data.error || 'RECEIPT_EMAIL_FAILED',
      field: data.field,
      message: error.response
        ? data.message || 'Could not email the receipt. Please try again.'
        : 'Could not reach GreenCharge. Check your connection and try again.',
      correlationId: data.correlationId || correlationId,
    };
  }
}

/**
 * Patch INFERRED receipt information (vehicle owner or fallback)
 * This stores receipt email without sending confirmation email
 * Used by FREE/FLEET license plate flow when email is inferred from vehicle registry
 * 
 * @param {string} sessionUid - Session UID
 * @param {object} receiptInfo - Receipt information
 * @param {string} receiptInfo.email - Customer's email address
 * @param {string} [receiptInfo.fullName] - Customer's full name (optional)
 * @param {string} [receiptInfo.source] - Source identifier (default: 'vehicle_registry')
 * @returns {Promise<object>} Response with updated session (no confirmation email sent)
 */
export async function patchReceiptInferred(sessionUid, { fullName, email, source = 'vehicle_registry', billingMode }) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] [FreeLicenseFlow] Storing inferred receipt contact:', {
      sessionUid,
      email,
      source,
      billingMode,
      hasName: !!fullName
    });

    const response = await axios.patch(
      `${SESSIONS_BASE}/${sessionUid}/receipt/inferred`,
      {
        fullName: fullName || null,
        email: email,
        source: source,
        billingMode: billingMode || null
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[SessionsAPI] [FreeLicenseFlow] Inferred receipt contact stored (NO confirmation email sent)');

    return {
      ok: true,
      receipt: response.data.receipt,
      email: response.data.email,
      correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] [FreeLicenseFlow] Failed to patch inferred receipt:', error.response?.data || error.message);

    return {
      ok: false,
      error: error.response?.data?.error || 'RECEIPT_INFERRED_PATCH_FAILED',
      message: error.response?.data?.message || error.message || 'Failed to store inferred receipt information',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Attach the vehicle the driver has just proven with its PIN to their
 * existing session. The typed-plate journey creates the session before the
 * PIN step, so this is where the verified vehicle becomes the session's
 * vehicle (owner receipt, vehicle billing mode).
 *
 * @returns {Promise<{ok: boolean, error?: string, billingMode?: string}>}
 */
export async function attachSessionVehicle(sessionUid, { vehicleId, vehiclePin }) {
  const correlationId = getCorrelationId();
  try {
    const response = await axios.post(
      `${SESSIONS_BASE}/${sessionUid}/vehicle`,
      { vehicleId, vehiclePin },
      { headers: { 'Content-Type': 'application/json', 'X-Correlation-Id': correlationId } }
    );
    return { ok: true, ...response.data, correlationId };
  } catch (error) {
    return authorizationAwareFailure(error, 'ATTACH_VEHICLE_FAILED', error.message, correlationId);
  }
}

/**
 * Pre-authorize payment for a session
 * This must be called AFTER session creation and BEFORE starting the charge
 * 
 * @param {string} sessionUid - Session UID
 * @param {object} params - Payment parameters
 * @param {number} params.amount - Amount in dollars (will be sent as-is)
 * @param {number} params.currency - Currency numeric code (default 840 for USD)
 * @param {object} params.paymentMethod - Payment method details
 * @returns {Promise<object>} Response with payment authorization result
 */
export async function preauthSession(sessionUid, { amount, currency = 52, paymentMethod }) {
  const correlationId = getCorrelationId();
  const idempotencyKey = uuidv4();

  try {
    console.log('[SessionsAPI] Pre-authorizing payment for session:', sessionUid);

    const response = await axios.post(
      `${SESSIONS_BASE}/${sessionUid}/payment/preauth`,
      {
        amount,
        currency,
        paymentMethod
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[SessionsAPI] Payment preauth successful:', {
      status: response.data.payment?.status,
      correlationId: response.data.correlationId
    });

    return {
      ok: true,
      ...response.data,
      correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] Payment preauth failed:', error.response?.data || error.message);

    return {
      ok: false,
      error: error.response?.data?.error || 'PREAUTH_FAILED',
      message: error.response?.data?.message || error.message || 'Payment authorization failed',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Start charging session
 * This triggers RemoteStartTransaction on the OCPP charger.
 * Must be called AFTER session creation (and payment authorization if
 * payments enabled).
 *
 * idTag handling — CRITICAL:
 *   - The QR/app flow MUST NOT send an idTag. The backend mints a
 *     server-side single-use `qr_…` token for the session and uses
 *     that as the OCPP idTag.
 *   - Only pass `idTag` when a real vehicle PIN was entered by the
 *     user. Placeholders like 'EV_USER' / 'test-id-tag' are rejected
 *     server-side and surface as a confusing "real PIN required"
 *     error, which the cable-connection prompt should mask.
 *
 * @param {string} sessionUid - Session UID
 * @param {object} [params]   - Optional start parameters
 * @param {string} [params.idTag] - Vehicle PIN (omit for QR/app flow)
 * @returns {Promise<object>} Response with start result
 */
export async function startSession(sessionUid, params = {}) {
  const correlationId = getCorrelationId();

  // Only include idTag in the wire payload when the caller supplied a
  // real (non-empty, non-placeholder) value. Default QR/app path
  // sends an empty body so the backend mints the qr_… token.
  const FORBIDDEN = new Set(['', 'EV_USER', 'test-id-tag', 'test_id_tag']);
  const rawIdTag = typeof params?.idTag === 'string' ? params.idTag.trim() : '';
  const body = {};
  if (rawIdTag && !FORBIDDEN.has(rawIdTag)) {
    body.idTag = rawIdTag;
  }

  try {
    console.log('[SessionsAPI] Starting charging session:', sessionUid, {
      includesIdTag: 'idTag' in body,
    });

    const response = await axios.post(
      `${SESSIONS_BASE}/${sessionUid}/start`,
      body,
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[SessionsAPI] Charging session started successfully');

    return {
      ok: true,
      session: response.data.session,
      // The API answers a start already in flight with idempotent: true.
      idempotent: response.data?.idempotent === true,
      correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] Failed to start session:', error.response?.data || error.message);

    // httpStatus is null when the request never got an HTTP answer.
    return authorizationAwareFailure(error, 'START_FAILED', 'Failed to start charging session', correlationId);
  }
}

/**
 * Cancel a session before charging starts
 * This voids any preauth and marks the session as voided
 * Can only be called when session is in 'pending' or 'authorized' status and hasn't started charging
 * 
 * @param {string} sessionUid - Session UID
 * @returns {Promise<object>} Response with cancel result
 */
export async function cancelSession(sessionUid) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] Canceling session:', sessionUid);

    const response = await axios.post(
      `${SESSIONS_BASE}/${sessionUid}/cancel`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[SessionsAPI] Session canceled successfully:', response.data);

    return {
      ok: true,
      session_uid: response.data.session_uid,
      status: response.data.status,
      correlationId: response.data.correlationId || correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] Failed to cancel session:', error.response?.data || error.message);

    // Handle 404 specially - session not found (already cancelled/expired/voided)
    if (error.response?.status === 404) {
      return {
        ok: false,
        notFound: true,
        error: 'SESSION_NOT_FOUND',
        message: 'Session not found or already cancelled',
        correlationId: error.response?.data?.correlationId || correlationId
      };
    }

    return {
      ok: false,
      error: error.response?.data?.error || 'CANCEL_FAILED',
      message: error.response?.data?.message || error.message || 'Failed to cancel session',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Stop a charging session with automatic retry
 * Sends OCPP RemoteStopTransaction command.
 * Retries up to 3 times on network/timeout errors (not on 4xx).
 * Uses a stable idempotency key so duplicate stop requests are safe.
 *
 * @param {string} sessionUid - Session UID
 * @param {object} [options] - Options
 * @param {number} [options.maxRetries=3] - Max retry attempts
 * @returns {Promise<object>} Response with stop result
 */
export async function stopSession(sessionUid, { maxRetries = 3 } = {}) {
  // Stable idempotency key per session — safe to retry without duplicate side effects
  const idempotencyKey = `stop-${sessionUid}`;
  const correlationId = getCorrelationId();

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[SessionsAPI] Stopping charging session (attempt ${attempt}/${maxRetries}):`, sessionUid);

      const response = await axios.post(
        `${SESSIONS_BASE}/${sessionUid}/stop`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Correlation-Id': correlationId,
            'Idempotency-Key': idempotencyKey
          },
          timeout: 15000 // 15s timeout per attempt
        }
      );

      console.log('[SessionsAPI] Stop command sent successfully:', response.data);

      return {
        ok: true,
        session_uid: response.data.session_uid,
        message: response.data.message,
        tx_id: response.data.tx_id,
        correlationId: response.data.correlationId || correlationId,
        attempts: attempt
      };
    } catch (error) {
      const status = error.response?.status;
      const isNetworkOrTimeout = !error.response || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
      const isServerError = status >= 500;
      const isRetryable = isNetworkOrTimeout || isServerError;

      console.error(`[SessionsAPI] Stop attempt ${attempt} failed:`, {
        status,
        error: error.response?.data?.error || error.message,
        retryable: isRetryable
      });

      // Don't retry client errors (4xx) — they won't succeed on retry
      if (!isRetryable || attempt >= maxRetries) {
        return {
          ok: false,
          error: error.response?.data?.error || 'STOP_FAILED',
          message: error.response?.data?.message || error.message || 'Failed to stop charging session',
          correlationId: error.response?.data?.correlationId || correlationId,
          attempts: attempt
        };
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 4000);
      console.log(`[SessionsAPI] Retrying stop in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

/**
 * Force-end a session when the charger is unresponsive
 * Bypasses OCPP — marks session as stopped on backend so finalization can proceed.
 * Used as escape hatch when RemoteStop fails or charger is offline.
 *
 * @param {string} sessionUid - Session UID
 * @returns {Promise<object>} Response with force-end result
 */
export async function forceEndSession(sessionUid) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] Force-ending session:', sessionUid);

    const response = await axios.post(
      `${SESSIONS_BASE}/${sessionUid}/force-end`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        },
        timeout: 15000
      }
    );

    console.log('[SessionsAPI] Force-end result:', response.data);

    return {
      ok: true,
      ...response.data,
      correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] Force-end failed:', error.response?.data || error.message);
    return {
      ok: false,
      error: error.response?.data?.error || 'FORCE_END_FAILED',
      message: error.response?.data?.message || error.message,
      correlationId
    };
  }
}

/**
 * Capture payment for a completed session
 * This finalizes the payment after charging completes
 * In mock mode, this should always succeed
 *
 * @param {string} sessionUid - Session UID
 * @param {object} params - Capture parameters
 * @param {number} params.amount - Final amount to capture in dollars
 * @returns {Promise<object>} Response with capture result
 */
export async function captureSessionPayment(sessionUid, { amount }) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] Capturing payment for session:', sessionUid, 'amount:', amount);

    const response = await axios.post(
      `${SESSIONS_BASE}/${sessionUid}/payment/capture`,
      {
        amount
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[SessionsAPI] Payment captured successfully');

    return {
      ok: true,
      payment: response.data.payment,
      correlationId: response.data.correlationId || correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] Failed to capture payment:', error.response?.data || error.message);

    return {
      ok: false,
      error: error.response?.data?.error || 'CAPTURE_FAILED',
      message: error.response?.data?.message || error.message || 'Failed to capture payment',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Trigger or retry server-side finalization for a completed session.
 * Idempotent: if already finalized, returns existing result.
 * Used when polling detects finalize_error or session stuck at 'completed'.
 *
 * @param {string} sessionUid - Session UID
 * @returns {Promise<object>} Response with finalization result
 */
export async function retriggerFinalization(sessionUid) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] Retriggering finalization for session:', sessionUid);

    const response = await axios.post(
      `${SESSIONS_BASE}/${sessionUid}/finalize`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[SessionsAPI] Finalization retrigger result:', response.data?.ok ? 'success' : 'failed');

    return {
      ok: response.data?.ok ?? true,
      ...response.data,
      correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] Finalization retrigger error:', error.response?.data || error.message);
    return {
      ok: false,
      error: error.response?.data?.error || 'FINALIZE_RETRY_FAILED',
      message: error.response?.data?.message || error.message,
      correlationId
    };
  }
}

/**
 * Mark a FREE session as paid to enable receipt sending
 * This is a minimal fix for FREE sessions stuck with payment_status='pending'
 * Only works for FREE sessions (billing_mode='FREE' or rate=0)
 * 
 * @param {string} sessionUid - Session UID
 * @returns {Promise<object>} Response with payment status update result
 */
export async function markFreeSessionAsPaid(sessionUid) {
  const correlationId = getCorrelationId();

  try {
    console.log('[FreeReceiptFix] Marking FREE session as paid:', sessionUid);

    const response = await axios.post(
      `${SESSIONS_BASE}/${sessionUid}/mark-free-paid`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[FreeReceiptFix] ✅ Session marked as paid:', {
      sessionUid: response.data.sessionUid,
      paymentStatus: response.data.paymentStatus,
      billingMode: response.data.billingMode,
      alreadyPaid: response.data.alreadyPaid
    });

    return {
      ok: true,
      sessionUid: response.data.sessionUid,
      paymentStatus: response.data.paymentStatus,
      billingMode: response.data.billingMode,
      alreadyPaid: response.data.alreadyPaid || false,
      correlationId
    };
  } catch (error) {
    console.error('[FreeReceiptFix] ❌ Failed to mark session as paid:', error.response?.data || error.message);

    return {
      ok: false,
      error: error.response?.data?.error || 'MARK_FREE_PAID_FAILED',
      message: error.response?.data?.message || error.message || 'Failed to mark FREE session as paid',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Submit final session summary data
 * This stores the client-calculated session totals for receipt generation
 * Should be called after payment capture succeeds
 * 
 * @param {string} sessionUid - Session UID
 * @param {object} summary - Session summary data
 * @param {number} summary.energyKwh - Total energy delivered in kWh
 * @param {number} summary.avgKw - Average power in kW
 * @param {number} summary.durationSeconds - Session duration in seconds
 * @param {number} summary.totalCost - Total cost charged
 * @param {string} summary.currency - Currency code (e.g., 'BBD', 'USD')
 * @param {string} summary.startedAt - Session start timestamp (ISO 8601)
 * @param {string} summary.endedAt - Session end timestamp (ISO 8601)
 * @param {number} summary.ratePerKwh - Rate per kWh applied
 * @param {number} summary.sessionFee - Session fee applied
 * @returns {Promise<object>} Response with updated session
 */
export async function submitSessionSummary(sessionUid, summary) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] Submitting session summary for session:', sessionUid);

    const response = await axios.patch(
      `${SESSIONS_BASE}/${sessionUid}/summary`,
      {
        energyKwh: summary.energyKwh,
        avgKw: summary.avgKw,
        durationSeconds: summary.durationSeconds,
        totalCost: summary.totalCost,
        currency: summary.currency,
        startedAt: summary.startedAt,
        endedAt: summary.endedAt,
        ratePerKwh: summary.ratePerKwh,
        sessionFee: summary.sessionFee
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[SessionsAPI] Session summary submitted successfully');

    return {
      ok: true,
      session: response.data.session,
      correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] Failed to submit session summary:', error.response?.data || error.message);

    return {
      ok: false,
      error: error.response?.data?.error || 'SUMMARY_SUBMIT_FAILED',
      message: error.response?.data?.message || error.message || 'Failed to submit session summary',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Get live session data (during charging)
 * Returns real-time estimates: energy, cost, power
 * Should be polled every 2s while charging is active
 * 
 * @param {string} sessionUid - Session UID
 * @returns {Promise<object>} Response with live estimates
 */
export async function getSessionLive(sessionUid) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] Getting live session data:', sessionUid);

    const response = await axios.get(
      `${SESSIONS_BASE}/${sessionUid}/live`,
      {
        headers: {
          'X-Correlation-Id': correlationId
        }
      }
    );

    return {
      ok: true,
      status: 200,
      data: response.data,
      correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] Failed to get live data:', error.response?.data || error.message);

    return {
      ok: false,
      status: error.response?.status || 500,
      error: error.response?.data?.error || 'LIVE_GET_FAILED',
      message: error.response?.data?.message || error.message || 'Failed to get live session data',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * The live estimate for a charging session (`greencharge.live-estimate.v1`;
 * sw-ev-api docs/SESSION_LIVE_ESTIMATE.md): the cost so far, the charge rate
 * and the time left, as the backend works them out from stored readings.
 * The monitoring step prints it; nothing here or there recalculates it.
 *
 * Never throws. A failure is `{ ok: false, ... }` and the caller keeps
 * showing "Calculating" (or its last good estimate).
 *
 * @param {string} sessionUid - Session UID
 * @returns {Promise<{ok: true, liveEstimate: object, correlationId: string}
 *   | {ok: false, error: string, message: string, httpStatus: number|null, correlationId: string}>}
 */
export async function getSessionLiveEstimate(sessionUid) {
  const correlationId = getCorrelationId();

  try {
    const response = await axios.get(
      `${SESSIONS_BASE}/${encodeURIComponent(sessionUid)}/live-estimate`,
      {
        headers: {
          'X-Correlation-Id': correlationId
        }
      }
    );

    const liveEstimate = response?.data?.liveEstimate;
    if (!liveEstimate || typeof liveEstimate !== 'object') {
      // A 200 without an estimate is not an answer (an older API, a proxy).
      return {
        ok: false,
        error: 'LIVE_ESTIMATE_UNAVAILABLE',
        message: 'The live estimate returned no data.',
        httpStatus: response?.status ?? null,
        correlationId: response?.data?.correlationId || correlationId
      };
    }

    return {
      ok: true,
      liveEstimate,
      correlationId: response.data.correlationId || correlationId
    };
  } catch (error) {
    const normalized = normalizeApiError(error, correlationId);
    return {
      ok: false,
      error: error?.response ? normalized.code : 'NETWORK_ERROR',
      message: normalized.message,
      httpStatus: normalized.rawStatus ?? null,
      correlationId: normalized.correlationId
    };
  }
}

/**
 * Get session summary (after charging stops)
 * Returns authoritative backend-calculated totals
 * 
 * Handles 409 status specially - means summary is still being finalized:
 * - Client should poll every 2s when 409 is returned
 * - After timeout (60s), show retry option
 * 
 * @param {string} sessionUid - Session UID
 * @returns {Promise<object>} Response with summary or 409 if not ready
 */
export async function getSessionSummary(sessionUid) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] Getting session summary:', sessionUid);

    const response = await axios.get(
      `${SESSIONS_BASE}/${sessionUid}/summary`,
      {
        headers: {
          'X-Correlation-Id': correlationId
        }
      }
    );

    return {
      ok: true,
      status: 200,
      data: response.data,
      correlationId
    };
  } catch (error) {
    const status = error.response?.status;

    // 409 Conflict = Summary not ready yet (not an error - normal during finalization)
    if (status === 409) {
      console.log('[SessionsAPI] Summary not ready (409) - still finalizing');
      return {
        ok: false,
        status: 409,
        error: null,
        data: null,
        message: 'Summary is being finalized',
        correlationId: error.response?.data?.correlationId || correlationId
      };
    }

    console.error('[SessionsAPI] Failed to get summary:', error.response?.data || error.message);

    return {
      ok: false,
      status: status || 500,
      error: error.response?.data?.error || 'SUMMARY_GET_FAILED',
      message: error.response?.data?.message || error.message || 'Failed to get session summary',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Submit session rating/feedback
 * This allows customers to rate their charging experience after session completion
 * 
 * @param {string} sessionUid - Session UID
 * @param {object} ratingData - Rating data
 * @param {number} ratingData.rating - Rating value (1-5 stars)
 * @param {string} [ratingData.comment] - Optional feedback comment
 * @param {string} [ratingData.email] - Optional customer email (from receiptInfo)
 * @param {string} [ratingData.fullName] - Optional customer name (from receiptInfo)
 * @returns {Promise<object>} Response with rating submission result
 */
export async function submitRating(sessionUid, { rating, comment, email, fullName }) {
  const correlationId = getCorrelationId();

  try {
    console.log('[RATING] Submitting rating for session:', sessionUid, { rating, hasComment: !!comment });

    const payload = { rating };
    
    // Add optional fields if present
    if (comment && comment.trim()) payload.comment = comment.trim();
    if (email && email.trim()) payload.email = email.trim();
    if (fullName && fullName.trim()) payload.fullName = fullName.trim();

    const response = await axios.post(
      `${SESSIONS_BASE}/${sessionUid}/rating`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[RATING] Rating submitted successfully');

    return {
      ok: true,
      rating: response.data.rating,
      correlationId: response.data.correlationId || correlationId
    };
  } catch (error) {
    console.error('[RATING] Failed to submit rating:', error.response?.data || error.message);

    return {
      ok: false,
      error: error.response?.data?.error || 'RATING_SUBMIT_FAILED',
      message: error.response?.data?.message || error.message || 'Failed to submit rating',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 3D SECURE PAYMENT FLOW
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Initiate 3DS pre-authorization for a session
 * Returns either a method form (fingerprinting step) or direct approval
 *
 * @param {string} sessionUid - Session UID
 * @param {object} params - Payment parameters
 * @param {number} params.amount - Amount in dollars
 * @param {object} params.paymentMethod - Payment method with card details
 * @returns {Promise<object>} Response with { step: "METHOD"|"COMPLETE", methodForm?, payment? }
 */
/**
 * Upper bound on one 3DS request. The API may ask the provider about a
 * previous attempt (30s) and then start the new one (30s).
 */
const THREEDS_REQUEST_TIMEOUT_MS = 75000;

export async function preauth3DSSession(sessionUid, { amount, paymentMethod }) {
  const correlationId = getCorrelationId();
  const idempotencyKey = uuidv4();

  try {
    console.log('[SessionsAPI] [3DS] Initiating 3DS preauth for session:', sessionUid);

    const response = await axios.post(
      `${SESSIONS_BASE}/${sessionUid}/payment/3ds-preauth`,
      { amount, paymentMethod },
      {
        // The API waits up to 30s on the gateway; never wait forever on it.
        timeout: THREEDS_REQUEST_TIMEOUT_MS,
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[SessionsAPI] [3DS] Preauth response:', {
      step: response.data.step,
      hasMethodForm: !!response.data.methodForm,
      correlationId: response.data.correlationId
    });

    return {
      ok: true,
      ...response.data,
      correlationId: response.data.correlationId || correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] [3DS] Preauth failed:', error.response?.data || error.message);

    return {
      ok: false,
      httpStatus: error.response?.status ?? null,
      // No response at all (timeout, network): the API may or may not have
      // started an authorization — the caller must reconcile, not retry.
      uncertain: !error.response,
      error: error.response?.data?.error || '3DS_PREAUTH_FAILED',
      message: error.response?.data?.message || error.message || '3DS payment authorization failed',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Continue 3DS flow after method notification (or timeout)
 * Returns challenge data (acsURL, cReq) or frictionless approval
 *
 * @param {string} sessionUid - Session UID
 * @returns {Promise<object>} Response with { step: "CHALLENGE"|"COMPLETE", acsURL?, cReq?, payment? }
 */
export async function continue3DSSession(sessionUid) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] [3DS] Continuing 3DS flow for session:', sessionUid);

    const response = await axios.post(
      `${SESSIONS_BASE}/${sessionUid}/payment/3ds-continue`,
      {},
      {
        timeout: THREEDS_REQUEST_TIMEOUT_MS,
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[SessionsAPI] [3DS] Continue response:', {
      step: response.data.step,
      hasAcsURL: !!response.data.acsURL,
      correlationId: response.data.correlationId
    });

    return {
      ok: true,
      ...response.data,
      correlationId: response.data.correlationId || correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] [3DS] Continue failed:', error.response?.data || error.message);

    return {
      ok: false,
      httpStatus: error.response?.status ?? null,
      uncertain: !error.response,
      error: error.response?.data?.error || '3DS_CONTINUE_FAILED',
      message: error.response?.data?.message || error.message || '3DS authentication failed',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Poll 3DS authentication status
 * Frontend calls this during challenge to detect when authentication completes
 *
 * @param {string} sessionUid - Session UID
 * @returns {Promise<object>} Response with { step, status: "pending"|"authorized"|"failed" }
 */
export async function get3DSStatus(sessionUid, { reconcile = false } = {}) {
  const correlationId = getCorrelationId();

  try {
    const response = await axios.get(
      `${SESSIONS_BASE}/${sessionUid}/payment/3ds-status`,
      {
        // reconcile=1 asks the API to check with the payment provider (used
        // after a timeout or an app resume, never on ordinary polls).
        params: reconcile ? { reconcile: 1 } : undefined,
        timeout: reconcile ? THREEDS_REQUEST_TIMEOUT_MS : 10000,
        headers: {
          'X-Correlation-Id': correlationId
        }
      }
    );

    return {
      ok: true,
      ...response.data,
      correlationId: response.data.correlationId || correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] [3DS] Status check failed:', error.response?.data || error.message);

    return {
      ok: false,
      error: error.response?.data?.error || '3DS_STATUS_FAILED',
      message: error.response?.data?.message || error.message || 'Failed to check 3DS status',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Check for active session on a specific connector
 * Used for session rehydration after page refresh or app backgrounding
 * 
 * @param {string} stationId - Station ID
 * @param {number} connectorId - Connector ID
 * @returns {Promise<object>} Response with active session or null
 */
export async function getActiveSessionForConnector(stationId, connectorId) {
  const correlationId = getCorrelationId();

  try {
    console.log('[SessionsAPI] Checking for active session on connector:', { stationId, connectorId });

    const response = await axios.get(
      `${API_BASE}/stations/${encodeURIComponent(stationId)}/active-session`,
      {
        headers: {
          'X-Correlation-Id': correlationId
        }
      }
    );

    const activeSession = response.data.activeSession;

    // If there's an active session, verify it matches the requested connector
    if (activeSession) {
      if (activeSession.connector_id === connectorId) {
        console.log('[SessionsAPI] Found active session on connector:', activeSession.session_uid);
        return {
          ok: true,
          activeSession,
          correlationId
        };
      } else {
        console.log('[SessionsAPI] Active session found but on different connector:', {
          requested: connectorId,
          actual: activeSession.connector_id
        });
        return {
          ok: true,
          activeSession: null,
          correlationId
        };
      }
    }

    console.log('[SessionsAPI] No active session found on connector');
    return {
      ok: true,
      activeSession: null,
      correlationId
    };
  } catch (error) {
    console.error('[SessionsAPI] Failed to check active session:', error.response?.data || error.message);

    // Handle 404 specially - station not found
    if (error.response?.status === 404) {
      return {
        ok: false,
        notFound: true,
        error: 'STATION_NOT_FOUND',
        message: 'Station not found',
        correlationId: error.response?.data?.correlationId || correlationId
      };
    }

    return {
      ok: false,
      error: error.response?.data?.error || 'ACTIVE_SESSION_CHECK_FAILED',
      message: error.response?.data?.message || error.message || 'Failed to check for active session',
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}
